/**
 * Типы pattern workflow.
 *
 * Pattern = MIDI-срез (один канал, фиксированный диапазон в тактах),
 * привязанный к одному из 128 слотов Fantom: 16 треков × 8 вариаций
 * (A..H). `PatternStore` хранит patterns, slices и ссылку на исходный
 * MIDI в текущей сессии.
 */

import type { Midi } from "@midi-fantom-utils/midi-core";

/** Восемь вариаций слота внутри трека (A..H). */
export type PatternSlot = "A" | "B" | "C" | "D" | "E" | "F" | "G" | "H";

export const PATTERN_SLOTS: readonly PatternSlot[] = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
] as const;

/** Идентификатор слота в формате "T{track}-{slot}", напр. "T01-A". */
export type PatternId = `T${string}-${PatternSlot}`;

export const formatPatternId = (track: number, slot: PatternSlot): PatternId => {
  const t = String(track).padStart(2, "0");
  return `T${t}-${slot}` as PatternId;
};

export type TrackNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16;

export const TRACK_COUNT = 16;

/** Диапазон среза (по тактам) и канал, из которого берутся ноты. */
export type SliceRange = {
  /** Начало среза в тактах (включительно), 1-based. */
  startMeasure: number;
  /** Конец среза в тактах (НЕ включительно). */
  endMeasure: number;
  /** Канал исходного MIDI (0..15), который попадает в срез. */
  channel: number;
};

/** Конкретный MIDI-паттерн (срез), привязанный к слоту Fantom. */
export type Pattern = {
  id: PatternId;
  track: TrackNumber;
  slot: PatternSlot;
  /** Имя, показываемое в UI и используемое как база имени файла на экспорте. */
  name: string;
  /** Длина в тактах (≤ 64 по hard cap Fantom). */
  lengthMeasures: number;
  /** Финальный MIDI, отправляемый в `encodeMidi` при экспорте. */
  midi: Midi;
  /** Канал исходного MIDI, сохранённый для справки. */
  sourceChannel: number;
  /** Число нот в паттерне. */
  noteCount: number;
};

/** Срез на таймлайне, потенциально привязанный к Pattern. */
export type Slice = {
  id: string;
  range: SliceRange;
  /** PatternId, если пользователь уже создал паттерн из этого среза. */
  patternId?: PatternId;
};

/** Стор сессии: срез + связанный паттерн (если есть). */
export type PatternStore = {
  /** Все срезы, созданные пользователем. */
  slices: ReadonlyArray<Slice>;
  /** Все паттерны (по `PatternId`). */
  patterns: ReadonlyMap<PatternId, Pattern>;
};

/** Результат `buildPattern` — либо успех, либо конфликт (слот занят). */
export type PatternBuildResult =
  | { ok: true; pattern: Pattern }
  | { ok: false; conflict: { existing: Pattern } };

/** Снимок одного паттерна для записи в manifest.json экспортируемого ZIP. */
export type PatternManifestEntry = {
  track: TrackNumber;
  slot: PatternSlot;
  file: string;
  lengthMeasures: number;
  noteCount: number;
};

/** Структура manifest.json в экспортируемом ZIP. */
export type PatternManifest = {
  project: string;
  generatedAt: string;
  patterns: PatternManifestEntry[];
};
